import { Col, Flex, Row, Typography } from "antd";
import LimitState from "./LimitState";
import { LimitDetailProps } from "./LimitDetails";
import AlertTypeId from "src/consts/AlertTypeId";
import AlertDTO from "src/models/AlertDTO";
import React from "react";
import EventDTO from "src/models/EventDTO";
import EventType from "src/consts/EventType";

interface LimitsStateViewProps{
    limitStates: EventDTO[];
    onClickLimit: (limit: LimitDetailProps | null | undefined) => void;
}

const { Text } = Typography;

const LimitsStateView : React.FC<LimitsStateViewProps> = ({ limitStates, onClickLimit }) => {
    
    const machineOverTemp = limitStates.find( l => l.eventType === EventType.MachineGearBox);
    const machineOverCurrent1 = limitStates.find( l => l.eventType === EventType.MachineOverCurrent1);
    const machineOverCurrent2 = limitStates.find( l => l.eventType === EventType.MachineOverCurrent2);
    const augerOverCurrent = limitStates.filter(l => l.eventType === EventType.AugerOverCurrent && l.triggerTimeUtc != null);
    const fanOverCurrent = limitStates.filter(l => l.eventType === EventType.FanOverCurrent && l.triggerTimeUtc != null);
    const remoteStop = limitStates.find(l => l.eventType === EventType.RemoteStop);    
    const plenumOverTemp = limitStates.find( l => l.eventType === EventType.PlenumOverTemp);
    const grainOverTemp = limitStates.find( l => l.eventType === EventType.GrainOverTemp); 

    return(
        <Flex justify='center' vertical style={{ maxWidth:'800px', paddingTop: '8px', paddingBottom: '8px' }}>
            <Row>
                <Text style={{ fontSize:'12px', fontWeight:'bold' }}>When a limit switch is tripped, the associated equipment will stop and must be reset.</Text>
            </Row>
            <br/>
            <Row wrap justify='center' gutter={[16, 16]}>      
                <Col 
                onClick={() => onClickLimit({
                    title: 'Machine motor over temp',
                    description: machineOverTemp?.description ?? 'No description available.',
                    perps: [machineOverTemp?.triggerReason ?? 'Unknown'],
                    tripped: machineOverTemp?.triggerTimeUtc != null
                })}>
                    <LimitState 
                    title={'Machine motor over temp'}
                    subTitle={
                        machineOverTemp?.triggerTimeUtc != null ? 
                        `${machineOverTemp?.alertCodeString}: Press here for details`
                        : 
                        'When tripped, fans shut down & won\'t restart'
                    }
                    tripped={machineOverTemp?.triggerTimeUtc != null}
                    />
                </Col>
                <Col 
                onClick={() => onClickLimit({
                    title: 'Machine 1 Motor Over Current',
                    description: machineOverCurrent1?.description ?? 'No description available.',
                    perps: [machineOverCurrent1?.triggerReason ?? 'Unknown'],
                    tripped: machineOverCurrent1?.triggerTimeUtc != null 
                })}>
                    <LimitState 
                    title={'Machine 1 Motor Over Current'}
                    subTitle={
                        machineOverCurrent1?.triggerTimeUtc != null ? 
                        `${machineOverCurrent1?.triggerTimeUtc}: Press here for details`
                        : 
                        'When tripped, machines shut down & won\'t restart'
                    }
                    tripped={machineOverCurrent1?.triggerTimeUtc != null}
                    />
                </Col>
                <Col 
                onClick={() => onClickLimit({
                    title: 'Machine 2 Motor Over Current',
                    description: machineOverCurrent2?.description ?? 'No description available.',
                    perps: [machineOverCurrent2?.triggerReason ?? 'Unknown'],
                    tripped: machineOverCurrent2?.triggerTimeUtc != null 
                })}>
                    <LimitState 
                    title={'Machine 2 Motor Over Current'}
                    subTitle={
                        machineOverCurrent2?.triggerTimeUtc != null ? 
                        `${machineOverCurrent2?.alertCodeString}: Press here for details`
                        : 
                        'When tripped, machines shut down & won\'t restart'
                    }
                    tripped={machineOverCurrent2?.triggerTimeUtc != null}
                    />
                </Col>
                <Col 
                onClick={() => onClickLimit({
                    title: 'Auger over current',
                    description: 'When tripped, auger shutdowns & won\'t restart',
                    perps: augerOverCurrent.map(a => a.triggerReason ?? 'Unknown'),
                    tripped: augerOverCurrent.length > 0
                })}>
                    <LimitState 
                    title={'Auger over current'}
                    subTitle={
                        augerOverCurrent?.length > 0 ? 
                        `${augerOverCurrent[0].alertCodeString}: Press here for details`
                        : 
                        'When tripped, machines shut down & won\'t restart'
                    }
                    tripped={augerOverCurrent.length > 0}
                    />
                </Col>
                <Col
                onClick={() => onClickLimit({
                    title: 'Fan over current',
                    description: 'When tripped, fans shutdown & won\'t restart',
                    perps: fanOverCurrent.map(f => f.triggerReason ?? 'Unknown'),
                    tripped: fanOverCurrent.length > 0
                })}
                >
                    <LimitState 
                    title={'Fan over current'}
                    subTitle={
                        fanOverCurrent?.length > 0 ? 
                        `${fanOverCurrent[0].alertCodeString}: Press here for details`
                        : 
                        'When tripped, fans shut down & won\'t restart'
                    }
                    tripped={fanOverCurrent.length > 0}
                    />
                </Col>
                <Col onClick={() => onClickLimit({
                    title: 'Remote Stop',
                    description: remoteStop?.description ?? 'No description available.',
                    perps: [remoteStop?.triggerReason ?? 'Unknown'],
                    tripped: remoteStop?.triggerTimeUtc != null 
                })
                }>
                    <LimitState 
                    title={'Remote Stop'}
                    subTitle={
                        remoteStop?.triggerTimeUtc != null  ? 
                        `${remoteStop?.alertCodeString}: Press here for details`
                        : 
                        'When tripped, machines shut down & won\'t restart'
                    }
                    tripped={remoteStop?.triggerTimeUtc != null }
                    />
                </Col>
                <Col onClick={() => onClickLimit({
                    title: 'Plenum Over Temp',
                    description: plenumOverTemp?.description ?? 'No description available.',
                    perps: [plenumOverTemp?.triggerReason ?? 'Unknown'],
                    tripped: plenumOverTemp?.triggerTimeUtc != null 
                })}>
                    <LimitState 
                    title={'Plenum Over Temp'}
                    subTitle={
                        plenumOverTemp?.triggerTimeUtc != null ? 
                        `${plenumOverTemp?.alertCodeString}: Press here for details`
                        : 
                        'When tripped, heaters shut down & won\'t restart'
                    }
                    tripped={plenumOverTemp?.triggerTimeUtc != null }
                    />
                </Col>
                <Col onClick={() => onClickLimit({
                    title: 'Grain Over Temp',
                    description: grainOverTemp?.description  ?? 'No description available.',
                    perps: [grainOverTemp?.triggerReason ?? 'Unknown'],
                    tripped: grainOverTemp?.triggerTimeUtc != null 
                })}>
                    <LimitState 
                    title={'Grain Over Temp'}
                    subTitle={
                        grainOverTemp?.triggerTimeUtc != null  ? 
                        `${grainOverTemp?.alertCodeString}: Press here for details`
                        : 
                        'When tripped, fans shut down & won\'t restart'
                    }
                    tripped={grainOverTemp?.triggerTimeUtc != null }
                    />
                </Col>
            </Row>
            <br/>
        </Flex>
    )
}

export default LimitsStateView;